<template>
	<div class="container" role="region" aria-label="Class Listing">
		<div class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ ariaSummary }}</div>

		<div v-for="(dayPart, dayPartIndex) in day.dayParts" :key="dayPartIndex">
			<div v-if="dayPart.startTimes.length > 0" class="planner-daypart" :class="'planner-daypart-'+dayPart.name.toLowerCase()">
				<div class="planner-daypart-leadin">
					Starts at
				</div>
				<div class="planner-daypart-label">
					{{ dayPart.name }}
				</div>
			</div>

			<div class="planner-times" v-for="(startTime, startTimeIndex) in dayPart.startTimes" :key="startTimeIndex" :class="{'future-time': startTime.timestamp > now}">
				<div class="planner-time" :class="{'planner-time-soon': startsSoon(startTime.timestamp)}">
					{{ startTime.time.split(' ')[0] }}<span class="time-ampm"> {{ startTime.time.split(' ')[1] }}</span>
					<div class="planner-time-label" v-if="startsSoon(startTime.timestamp)">Starts soon</div>
				</div>

				<div class="planner-entry" data-testid="classCell" v-for="(activity, activityIndex) in startTime.activities" :key="activityIndex" :class="{ 'card-danger': activity.isCanceled }">
					<div class="planner-row">
						<div class="planner-col-md-3">
							<div class="planner-entry-title">
								<span class="text-danger" v-if="activity.isCanceled">Canceled </span>
								<a :href="isProspectMode ? '' : `${classDetailsUrl}?eventId=${activity.id}`" @click="$emit('classClicked', $event, activity.name, activity.description)" data-testid="classLink">
									<span v-if="activity.isCanceled"><s>{{ activity.name }}</s></span>
									<span v-else>{{ activity.name }}</span>
								</a>
								<span v-if="activity.isPaidClass"><span class="ico-pay mylt-green" data-testid="isPaidClass"></span><span class="sr-only"> (paid class)</span></span>
							</div>
						</div>
						<div class="planner-col-md-2 font-weight-bold" data-testid="startAndEndTime" v-html="parseTimes(startTime.time, activity.endTime)"></div>
						<div class="planner-col-md-2">
							<span v-if="activity.instructors.length > 0" v-html="buildInstructors(activity.instructors)"></span>
						</div>
						<div class="planner-col-md-3">
							{{ activity.location }}
						</div>
						<div class="planner-entry-cta planner-col-md-2">
							<a :href="isProspectMode ? '' : `${classDetailsUrl}?eventId=${activity.id}`" v-if="startTime.timestamp > now && !isProspectMode" @click="$emit('classClicked', $event, activity.name, activity.description)" data-testid="reserveLink">{{ activity.cta }}</a>
						</div>
					</div>
				</div>

			</div>
		</div>
		<div v-if="day.total === 0" class="planner-entry no-results">No results for this day.
			<span v-if="isProspectMode"><a @click="showProspectFilters" role="button" data-anchor-button>Adjust your selection</a> to view more classes.</span>
		</div>

	</div>
</template>

<script>
import { defineComponent } from 'vue';
// import moment from 'moment'; // TODO: Disabled auto-scroll until notification center solution is in place (ESR-3496)
import { Helpers } from '@/common/helpers';
import { FrameworkService } from '@/services/framework.service';
import { clockObservable$, dateProvider } from '@/services/clock.service';

let clockSubscription;

export default defineComponent({
	name: 'DayViewSub',
	props: {
		day: {
			default: null,
			type: Object
		},
		isProspectMode: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			now: dateProvider(),
			classDetailsUrl: ''
		};
	},
	computed: {
		dayText() {
			const selectedDayMoment = Helpers.parseDateToMoment(this.day.day);
			return selectedDayMoment.format('dddd, MMMM D');
		},
		ariaSummary() {
			if (this.day) {
				return `${this.day.total} class results for ${this.dayText}`;
			}
			return 'Loading class schedules';
		}
	},
	// watch: { // TODO: Disabled auto-scroll until notification center solution is in place (ESR-3496)
	// 	day: function(newDay, oldDay) {
	// 		if (newDay.day !== oldDay.day) {
	// 			this.scrollPage();
	// 		}
	// 	}
	// },
	created() {
		clockSubscription = clockObservable$
			.subscribe(m => {
				this.now = m;
			});
	},
	async mounted() {
		this.completeMounting();
	},
	destroyed() {
		clockSubscription.unsubscribe();
	},
	methods: {
		completeMounting() {
			// this.scrollPage(); // TODO: Disabled auto-scroll until notification center solution is in place (ESR-3496)
			this.classDetailsUrl = FrameworkService.clubUrl('classDetails');
		},
		buildInstructors(instructors) {
			return Helpers.buildInstructors(instructors);
		},
		parseTimes(startTime, endTime) {
			return Helpers.parseTimes(startTime, endTime);
		},
		startsSoon(timestamp) {
			return (timestamp > this.now && timestamp < this.now + 1800000);
		},
		showProspectFilters() {
			$('#filtersSub').collapse('show');
			$('.inline-mode-scroller').scrollTop(0);
		}
		//, // TODO: Disabled auto-scroll until notification center solution is in place (ESR-3496)
		// scrollPage(focusTime) { // focusTime should only be set to true if coming from a skip link, since moving focus down the page wouldn't be desirable in other cases (changing weeks would take focus out of the date nav, for example)
		// 	if (FrameworkService.isProspectExperience()) return; // Don't auto-scroll in Prospect Mode
		// 	this.$nextTick(function() { // $nextTick to ensure DOM has rendered (to do getElementsByClassName)
		// 		let timeToScrollTo;

		// 		if (this.day.day === momentProvider().format('YYYY-MM-DD')) {
		// 			let futureTimes = document.getElementsByClassName('future-time');
		// 			if (futureTimes.length > 0) {
		// 				timeToScrollTo = futureTimes[0]; // Scroll to the first future time
		// 			} else {
		// 				let allTimes = document.getElementsByClassName('planner-times');
		// 				timeToScrollTo = allTimes.length > 0 ? allTimes[allTimes.length-1] : null; // No future times, so scroll to the last of the day
		// 			}
		// 		} else {
		// 			let allTimes = document.getElementsByClassName('planner-times');
		// 			timeToScrollTo = allTimes.length > 0 ? allTimes[0] : null; // For non-today dates, scroll to first time
		// 		}

		// 		if (timeToScrollTo) {
		// 			if (focusTime) {
		// 				timeToScrollTo.setAttribute('tabindex', '-1');
		// 				timeToScrollTo.focus();
		// 			}

		// 			let stickyHeadersHeight = document.getElementsByClassName('planner-datebar')[0].offsetHeight + document.getElementsByClassName('planner-daypart')[0].offsetHeight;
		// 			let scrollToY = Helpers.getPosition(timeToScrollTo)[1] - stickyHeadersHeight;
		// 			window.scrollTo(0, scrollToY);
		// 		}
		// 	});
		// }
	}
});
</script>

<style scoped lang="scss">
@media print {
	.planner-time {
		position: static;
	}
}
</style>
